<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '500px'"
		@click:outside="closeDialog"
		@keydown.esc="closeDialog"
		@keydown.enter="saveMountConfig"
	>
		<v-card data-test="mount-data-source-dialog">
			<v-card-title class="dialog-header sgnl-subtitle-medium"
				>Mount Data Source
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="closeDialog"
					data-test="close-mount-data-source-dialog-button"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body">
				<div class="input-group">
					<div class="field-label">Source</div>
					<v-btn
						v-if="!mountConfig || !mountConfig?.ResourceID"
						variant="outlined"
						@click="showMountDataSourcesDialog = true"
						data-test="select-data-source-button"
					>
						Select Data Source
					</v-btn>
					<DataSourcePickerCard
						v-else-if="mountConfig?.ResourceType"
						:setAsSource="true"
						:dataSourceID="mountConfig.ResourceID"
						:dataSourceType="mountConfig.ResourceType"
						@reset-data-source="unselectDataSource"
					/>
				</div>
				<div class="input-group">
					<div class="field-label">Destination</div>
					<v-text-field
						v-model="mountConfig.Location"
						maxlength="500"
						placeholder="Mount location"
						:hint="
							!mountConfig.ResourceID
								? `First choose a data source to be mounted at ${mountConfig.Location}/`
								: `Data source will be mounted at ${mountConfig.Location}/`
						"
						:error-messages="
							!validMountLocation
								? `Mounting at ${mountConfig.Location}/ not allowed. Please set a valid path.`
								: ''
						"
						data-test="data-source-mount-destination"
					/>
				</div>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="resetMountConfig"
				>
					Reset to Default
				</v-btn>
				<v-btn
					variant="outlined"
					@click="removeMountConfig"
					data-test="remove-mount-config-button"
				>
					Remove Mount
				</v-btn>
				<v-btn
					variant="flat"
					color="primary"
					@click="saveMountConfig"
					:disabled="!validConfig"
					data-test="save-mount-config-button"
				>
					Save Mount
				</v-btn>
			</v-card-actions>
		</v-card>
		<DataSourceListDialog
			v-if="showMountDataSourcesDialog"
			:showDialog="showMountDataSourcesDialog"
			@close-dialog="showMountDataSourcesDialog = false"
			@set-data-source="setDataSource"
			:dataSourceExcludeList="dataSourceExcludeList"
		/>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";

// Types
import { DataSourceTypeE, isLocalMountConfig, LocalMountConfig } from "@/types/api/dataSources";

// Components
import DataSourceListDialog from "@/components/DataSources/DataSourceListDialog.vue";
import DataSourcePickerCard from "@/components/DataSources/DataSourcePickerCard.vue";

type ComponentData = {
	mountConfig: Partial<LocalMountConfig>;
	showMountDataSourcesDialog: boolean;
};

export default defineComponent({
	name: "MountDataSourcesDialog",
	components: { DataSourcePickerCard, DataSourceListDialog, Close16 },
	props: {
		showDialog: { type: Boolean, default: false },
		initialMountConfig: { type: Object as PropType<Partial<LocalMountConfig>> },
		dataSourceExcludeList: { type: Array, default: () => [] },
	},
	emits: {
		"set-data-sources": (_mountConfig: LocalMountConfig) => true,
		"remove-data-sources": (_data: null) => true,
		"reset-data-sources": (_mountConfig: undefined) => true,
		"close-dialog": () => true,
		"update:showDialog": (_value: boolean) => true,
	},
	data: (): ComponentData => ({
		mountConfig: {
			Location: undefined,
			ResourceID: undefined,
			ResourceType: undefined,
		},
		showMountDataSourcesDialog: false,
	}),
	methods: {
		closeDialog() {
			this.$emit("close-dialog");
		},
		setDataSource(dataSource: LocalMountConfig) {
			this.mountConfig.ResourceID = dataSource.ResourceID;
			this.mountConfig.ResourceType = dataSource.ResourceType;

			if (
				this.mountConfig.ResourceType === DataSourceTypeE.Drive &&
				(this.mountConfig.Location === "" || this.mountConfig.Location === undefined)
			) {
				this.mountConfig.Location = ".";
			}
		},
		unselectDataSource() {
			this.mountConfig.ResourceID = undefined;
			this.mountConfig.ResourceType = undefined;
		},
		saveMountConfig() {
			if (isLocalMountConfig(this.mountConfig) && this.validMountLocation) {
				this.$emit("set-data-sources", this.mountConfig);
				this.$posthog?.capture("editor_data_source_mounted");
			}
		},
		removeMountConfig() {
			this.$emit("remove-data-sources", null);
		},
		resetMountConfig() {
			this.$emit("reset-data-sources", undefined);
		},
	},
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		validConfig(): boolean {
			return isLocalMountConfig(this.mountConfig) && this.validMountLocation;
		},
		validMountLocation(): boolean {
			if (!this.mountConfig.ResourceID) {
				return true;
			}

			if (this.mountConfig.Location !== undefined) {
				if (this.mountConfig.Location === "") {
					return false;
				}

				if (this.mountConfig.Location.includes("..")) {
					return false;
				}

				if (this.mountConfig.Location.includes("\\")) {
					return false;
				}

				if (this.mountConfig.Location.startsWith("/")) {
					return false;
				}

				if (this.mountConfig.ResourceType !== DataSourceTypeE.Drive) {
					if (this.mountConfig.Location.includes(".")) {
						return false;
					} else {
						return true;
					}
				}
				return true;
			}
			return false;
		},
	},
	mounted() {
		if (isLocalMountConfig(this.initialMountConfig)) {
			this.mountConfig = { ...this.initialMountConfig };
		} else {
			this.mountConfig = {
				Location: "",
				ResourceID: undefined,
				ResourceType: undefined,
			};
		}
	},
});
</script>
